import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Dialog, IconButton } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentNoticeAlertItem } from '../../utils/cancellationAlert';

interface PaymentNoticeDialogProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  alerts?: PaymentNoticeAlertItem[];
  submitLabel?: string;
  cancelLabel?: string;
}

const titleStyle: React.CSSProperties = {
  fontFamily: '"Be Vietnam Pro", sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  letterSpacing: 0,
};

const renderWarningIcon = () => (
  <div className="flex justify-center">
    <div className="w-12 h-12 rounded-full bg-[#FEF3F2] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full bg-[#FEE4E2] flex items-center justify-center">
        <WarningAmberIcon sx={{ width: 20, height: 20, color: '#F04438' }} />
      </div>
    </div>
  </div>
);

const PaymentNoticeDialog: React.FC<PaymentNoticeDialogProps> = ({
  open,
  onCancel,
  onSubmit,
  isSubmitting = false,
  alerts,
  submitLabel,
  cancelLabel,
}) => {
  const { t } = useTranslation();
  const displayAlerts = alerts || [];
  if (!displayAlerts.length) return null;

  const showRestaurantName = displayAlerts.length > 1;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      sx={{
        '& .MuiPaper-root': {
          width: 'calc(100% - 48px)',
          maxWidth: '360px',
          borderRadius: '8px',
        },
      }}
    >
      <div className="p-4" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
        <div className={`mb-4 ${displayAlerts.length > 1 ? 'max-h-[360px] overflow-y-auto pr-1' : ''}`}>
          {displayAlerts.map((alert, index) => (
            <div key={`${alert.title}-${alert.restaurantName || index}`} className={index > 0 ? 'mt-4 border-t border-gray-200 pt-4' : ''}>
              {index === 0 ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-gray-900" style={titleStyle}>
                    {alert.title}
                  </p>
                  <IconButton onClick={onCancel} disabled={isSubmitting} size="small" sx={{ padding: '2px' }}>
                    <CloseIcon sx={{ width: 24, height: 24, color: '#667085' }} />
                  </IconButton>
                </div>
              ) : (
                <p className="text-gray-900" style={titleStyle}>
                  {alert.title}
                </p>
              )}
              {showRestaurantName && alert.restaurantName && <p className="mt-1 text-sm font-semibold text-green-700">{alert.restaurantName}</p>}
              <div className="mt-3 mb-3">{renderWarningIcon()}</div>
              <p className="text-xs leading-[18px] text-gray-700 font-medium whitespace-pre-line">{alert.body}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className="w-full rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitLabel || t('ORDER_PAYMENT_NOTICE_PROCEED')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {cancelLabel || t('ORDER_PAYMENT_NOTICE_CANCEL')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PaymentNoticeDialog;
